import React, { useEffect, useState } from 'react';

const ExamUnionEarningCalculator = ({ param1, param2, maxLevel = 6, refsPerLevel = 5 }) => {
  const levels = Array.from({ length: maxLevel }, (_, i) => i + 1);
  const [overallCommission, setOverallCommission] = useState(0);
  const [lang, setLang] = useState("en");

  const param2Number = Number(param2);
  let commissionRates = param2Number === 10
    ? [0.10, 0.02, 0.005, 0.005, 0.005, 0.005]
    : [0.20, 0.02, 0.02, 0.02, 0.02, 0.02];

  // Limit commissionRates array to maxLevel
  commissionRates = commissionRates.slice(0, maxLevel);

  const calculateCommission = (price, level) => {
    const rate = commissionRates[level - 1];
    const commission = price * rate * Math.pow(refsPerLevel, level);
    return commission.toFixed(2);
  };

  useEffect(() => {
    let totalCommission = 0;
    levels.forEach((level) => {
      const rate = commissionRates[level - 1];
      const commission = param1 * rate * Math.pow(refsPerLevel, level);
      totalCommission += commission;
    });
    setOverallCommission(totalCommission.toFixed(2));
  }, [param1, param2, maxLevel, refsPerLevel]);

  const convertToIndianWords = (num) => {
    const a = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
      'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
    const b = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

    const formattedNum = Math.floor(Number(num)).toString();
    if (!formattedNum || isNaN(formattedNum)) return 'Invalid number';
    if (formattedNum === '0') return 'Zero';
    if (formattedNum.length > 12) return 'number too large';

    let words = [];
    const length = formattedNum.length;

    if (length > 7) {
      const crorePart = formattedNum.slice(0, length - 7);
      if (crorePart) words.push(getWords(crorePart) + ' Crore');
    }
    if (length > 5) {
      const lakhPart = formattedNum.slice(length - 7, length - 5);
      if (lakhPart) words.push(getWords(lakhPart) + ' Lakh');
    }
    if (length > 3) {
      const thousandPart = formattedNum.slice(length - 5, length - 3);
      if (thousandPart) words.push(getWords(thousandPart) + ' Thousand');
    }
   if (length > 2) {
    const hundredPart = formattedNum.slice(length - 3, length - 2);
    if (hundredPart !== '0') {
        words.push(getWords(hundredPart) + ' hundred');
    }
    }

    const lastTwoDigits = formattedNum.slice(-2);
    if (lastTwoDigits) words.push(getWords(lastTwoDigits));

    return words.join(' ').trim();
  };

  const getWords = (numStr) => {
    const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
      'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    if (numStr.length === 1) return a[Number(numStr)];
    if (numStr.length === 2) {
      if (numStr[0] === '1') return a[Number(numStr)];
      return (b[Number(numStr[0])] + (numStr[1] !== '0' ? ' ' + a[Number(numStr[1])] : '')).trim();
    }
    return '';
  };

  return (
    <div className="w-full inset-0 bg-black px-2 py-0 rounded-lg shadow-lg mt-1 overflow-x-auto">
      <table className="table-auto w-full text-white text-sm border border-blue-500">
        <thead>
          <tr>
            <th className="px-2 py-2 text-left text-xs lg:text-sm ">Level : Rate</th>
            <th className="px-2 py-2 text-left text-xs lg:text-sm ">Referrals : Incentive/Referral</th>
            <th className="px-2 py-2 text-left text-xs lg:text-sm ">Total Earning (₹)</th>
          </tr>
        </thead>
        <tbody>
          {levels.map((level) => (
            <tr key={level}>
              <td className="border px-2 py-2 text-left text-xs lg:text-sm ">
                {level} : {(commissionRates[level - 1] * 100).toFixed(2)}%
              </td>
              <td className="border px-2 py-2 text-left text-xs lg:text-sm ">
                {refsPerLevel}<sup>{level}</sup> = {Math.pow(refsPerLevel, level)} : [<span className="text-yellow-200">{(param1 * commissionRates[level - 1]).toFixed(2)}</span>]
              </td>
              <td className="border px-2 py-2 text-left text-xs lg:text-sm ">{calculateCommission(param1, level)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="text-yellow-200 text-xs lg:text-sm mt-2">
        Total Estimated Incentive: ₹ {Number(overallCommission).toFixed(2).toLocaleString('en-IN')}
      </p>
      <p className="text-white text-xs lg:text-sm border-b border-blue-400">
        In words: {convertToIndianWords(overallCommission)} Rupees
      </p>
    <div>
    {/* Toggle Button */}
    <div className="flex justify-end mb-2 mt-2">
        <button
        type="button"
        onClick={() => setLang(lang === "en" ? "hi" : "en")}
        className="text-xs px-3 py-1 bg-blue-500 text-white rounded"
        >
        {lang === "en" ? "Read in Hindi" : "Read in English"}
        </button>
    </div>

    {/* Heading */}
    <p className="text-yellow-200 mt-2">
        {lang === "en" ? "Rules, Terms & Disclaimers:" : "नियम, शर्तें एवं अस्वीकरण:"}
    </p>
    {/* Content */}
    <p className="text-gray-200 mt-2 text-xs lg:text-sm">
        <ol className="flex flex-col justify-left list-decimal list-inside space-y-1 [&_li]:text-gray-200">
            {lang === "en" ? (
                <>
                <li>This income estimator is for illustrative purposes only and does not guarantee any income. Actual earnings depend entirely on product sales and individual effort.</li>
                <li>The number of referral sales per level is not fixed. You may achieve zero or any number of sales depending on your efforts and market response.</li>
                <li>The illustration assumes {refsPerLevel} referral sales per level to demonstrate that your product cost may be effectively recovered through successful sales.</li>
                <li>You may earn passive (indirect) income from sales generated within your network, up to {maxLevel} levels, subject to the applicable commission structure.</li>
                <li>Income earned from direct referrals is treated as active income and is credited based on successful sales transactions.</li>
                <li>Passive (indirect) income can be claimed once the accumulated amount reaches a minimum threshold of ₹5,000.</li>
                <li>Eligible passive income for a given month will be processed and transferred to your account in the second week of the following month, after adjusting any interim payouts.</li>
                <li>All earnings are estimated in Indian Rupees (₹).</li>
                <li>GST (currently 18%) will be applied on applicable service fees.</li>
                <li>TDS will be deducted as per applicable income tax laws. A valid PAN card is required. In case of minors, the parent/guardian’s PAN details must be provided.</li>
                <li>The model is designed to enable micro-income generation for customers by channeling part of the marketing expenditure toward user-driven product promotion, rather than relying solely on third-party digital marketing platforms.</li>
                <li>This is an effort to help customers earn small additional income by sharing products, as part of the company’s marketing spend is passed on to users instead of external platforms.</li>
                <li>This model operates as a product-based direct selling system and is intended to comply with the Consumer Protection (Direct Selling) Rules, 2021 and applicable laws in India.</li>
                </>
            ) : (
                <>
                <li>यह आय अनुमान केवल उदाहरण हेतु है और किसी आय की गारंटी नहीं देता। वास्तविक आय पूरी तरह उत्पाद बिक्री और व्यक्तिगत प्रयास पर निर्भर करती है।</li>
                <li>प्रत्येक स्तर पर रेफरल बिक्री की संख्या निश्चित नहीं है। आपके प्रयास और बाज़ार की प्रतिक्रिया के आधार पर यह शून्य या किसी भी संख्या में हो सकती है।</li>
                <li>यह उदाहरण प्रति स्तर {refsPerLevel} रेफरल बिक्री मानकर बनाया गया है, ताकि यह दिखाया जा सके कि सफल बिक्री के माध्यम से उत्पाद की लागत प्रभावी रूप से वसूल की जा सकती है।</li>
                <li>आप अपने नेटवर्क में उत्पन्न बिक्री से अधिकतम {maxLevel} स्तर तक निष्क्रिय (इनडायरेक्ट) आय कमा सकते हैं, जो लागू कमीशन संरचना पर निर्भर है।</li>
                <li>प्रत्यक्ष रेफरल से अर्जित आय को सक्रिय आय माना जाता है और यह सफल बिक्री लेन-देन के आधार पर प्रदान की जाती है।</li>
                <li>निष्क्रिय (इनडायरेक्ट) आय का दावा तब किया जा सकता है जब कुल राशि न्यूनतम ₹5,000 तक पहुँच जाए।</li>
                <li>किसी माह की पात्र निष्क्रिय आय को अगले माह के दूसरे सप्ताह में, किसी भी अंतरिम भुगतान को समायोजित करने के बाद, आपके खाते में स्थानांतरित किया जाएगा।</li>
                <li>सभी आय भारतीय रुपये (₹) में अनुमानित हैं।</li>
                <li>लागू सेवा शुल्क पर जीएसटी (वर्तमान में 18%) लागू होगा।</li>
                <li>टीडीएस लागू आयकर नियमों के अनुसार काटा जाएगा। एक वैध पैन कार्ड आवश्यक है। नाबालिग के मामले में, अभिभावक का पैन विवरण देना होगा।</li>
                <li>यह मॉडल ग्राहकों के लिए सूक्ष्म आय (माइक्रो-इनकम) के अवसर प्रदान करने हेतु डिज़ाइन किया गया है, जिसमें कंपनी के विपणन व्यय का एक हिस्सा उपयोगकर्ताओं के माध्यम से उत्पाद प्रचार में लगाया जाता है, बजाय केवल तृतीय-पक्ष डिजिटल प्लेटफॉर्म पर खर्च करने के।</li>
                <li>यह ग्राहकों को उत्पाद साझा करके अतिरिक्त छोटी आय अर्जित करने में सहायता करने का एक प्रयास है, क्योंकि कंपनी अपने मार्केटिंग खर्च का एक हिस्सा बाहरी प्लेटफॉर्म के बजाय उपयोगकर्ताओं को प्रदान करती है।</li>
                <li>यह मॉडल उत्पाद-आधारित डायरेक्ट सेलिंग प्रणाली के रूप में संचालित होता है और भारत में लागू उपभोक्ता संरक्षण (डायरेक्ट सेलिंग) नियम, 2021 तथा अन्य लागू कानूनों का पालन करने के उद्देश्य से बनाया गया है।</li>
                </>
            )}
        </ol>
    </p>
    </div>      
    </div>
  );
};

export default ExamUnionEarningCalculator;
